
import {isEmpty, cloneDeep, isEqual, filter, includes} from 'lodash';
import { diff } from 'deep-diff';

function checkSame(var1, var2){
    // Compares two variables and returns whether there is any difference between the two
    if(typeof(var1) !== typeof(var2)){
        return false;
    }
    if(var1 instanceof Array){
        if(var1.length !== var2.length){
            return false;
        }
        for(let index = 0; index < var1.length; index ++){
            if(checkSame(var1[index], var2[index]) === false){
                return false;
            }
        }

    }
    else if(var1 instanceof Object){
        for (const [key, value] of Object.entries(var1)) {
            // ignore private field
            if(key.startsWith('_')){
                continue;
            }
            if(checkSame(value, var2[key]) === false){
                return false;
            }
        }
    }
    else if(var1 !== var2){
        return false;
    }
    return true;
}


function createUuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
}

class ResourceQueries{
    constructor(value){
        this.model_uid = value.uid;
        this.model_name = value.name;

        for(let array of this.arrayNames){
            this[array] = [];
        }
    }

    get arrayNames (){
        return [
            'search_fields',
            'filter_fields',
            'display_fields',
            'export_fields',
            'ordering_fields',
        ];
    }

    update (resourceQuery){
        for(let array of this.arrayNames){
            if (resourceQuery[array]){
                this[array] = resourceQuery[array];
            // If 'ordering_fields' is not in resourceQuery, add the display
            // fields to ordering_fields
            } else if (array == 'ordering_fields'){
                this.ordering_fields = resourceQuery['display_fields'];
            }
        }
    }
    setName(value) {
        this.model_name = value;
    }
    updateFieldName (newName, previousFieldName){
        for(let array of this.arrayNames){
            if(this[array].includes(previousFieldName)){
                this[array].splice(this[array].indexOf(previousFieldName), 1, newName)
            }
        }
    }
    removeField (fieldNameToRemove){
        for(let array of this.arrayNames){
            if(this[array].includes(fieldNameToRemove)){
                this[array].splice(this[array].indexOf(fieldNameToRemove), 1)
            }
        }
    }
}

export { ResourceQueries };

class Permission{
    constructor(value){
        this.model_uid = value.uid;
        this.model_name = value.name;
        this.lookups = [];
        if(this.model_name === 'User') {
            this.minimum_levels = {
                create: 'manager',
                retrieve: 'authenticated',
                update: 'authenticated',
                delete: 'manager',
            };
        } else if (this.model_name === 'Group') {
            this.minimum_levels = {
                create: 'manager',
                retrieve: 'authenticated',
                update: 'manager',
                delete: 'admin',
            };
        } else {
            this.minimum_levels = {
                create: 'superuser',
                retrieve: 'superuser',
                update: 'superuser',
                delete: 'superuser',
            };
        }
    }

    setName(value) {
        this.model_name = value;
    }

    setMinimumLevels(value) {
        this.minimum_levels = value;
    }

    setLookups(value) {
        this.lookups = value || [];
    }

    updateLookupName (modelList){
        for (var i = this.lookups.length - 1; i >= 0; i--) {
            let lookup = this.lookups[i];
            let relatedModel = modelList.find((model) => model.uid === lookup.model_uid);
            lookup.model_name = relatedModel.name;
            lookup.model_uid = relatedModel.uid;
        }
    }

    get createLevel() {
        return this.minimum_levels.create;
    }
    get retrieveLevel() {
        return this.minimum_levels.retrieve;
    }
    get updateLevel() {
        return this.minimum_levels.update;
    }
    get deleteLevel() {
        return this.minimum_levels.delete;
    }

    get isScoped() {
        return this.lookups.length !== 0;
    }
}

class Field{
    constructor(value){
        this.attributes = value.attributes;
        this.datatype = value.datatype;
        this.name = value.name;
        this.provisionalFieldUid = createUuidv4();
    }
    setAttributes (){
        this.attributes = {};
    }
}

class Model{
    constructor(value){
        this.fields = [];
        for(let field of value.fields){
            this.fields.push(new Field(field));
        }
        this.fields.sort((a,b) => {
            if(a.name < b.name) { return -1; }
            if(a.name > b.name) { return 1; }
            return 0;
        });
        this.name = value.name;
        this.uid = value.uid;
        this.description = value.description;
        this.representation_field = value.representation_field || '';
        this.is_default_public = value.is_default_public || false;
        this.model_description = value.model_description || '';
    }
    addField (){
        let hasBlanckField = this.fields.some((prop) => prop.name === '');
        if(hasBlanckField) return;
        return this.fields.push(new Field({name: '', datatype: 'char', attributes: {}}));
    }
    setRepresentationField (value){
        this.representation_field = value;
    }

    get listFieldsName (){
        return this.fields.map((field) => field.name);
    }
}

class Modeling{
    constructor(value, listRelDatatype, version){
        let schema = cloneDeep(value);
        let models = [];
        let permissions = [];
        let resourceQueries = [];
        if(isEmpty(schema)) return;
        for(let model of schema.manifest.data_modeling.models){
            models.push(new Model(model));
            permissions.push(new Permission(model));
            resourceQueries.push(new ResourceQueries(model));
        }
        this.sortBy(models, 'name');
        models.map(model=>{
            this.sortBy(model.fields, 'name');
        });
        this.sortBy(permissions, 'model_name');
        this.sortBy(resourceQueries, 'model_name');
        if(schema.manifest.data_modeling.permissions.length !== 0) {
            // If initial permissions is not empty, update values
            for(let permission of permissions){
                let updatePermission = schema.manifest.data_modeling.permissions.find((p) => {
                    return p.model_uid === permission.model_uid;
                });
                if(updatePermission !== undefined ){
                    permission.setMinimumLevels(updatePermission.minimum_levels);
                    permission.setLookups(updatePermission.lookups);
                }
            }
        }
        if(schema.manifest.data_modeling.resource_queries.length !== 0) {
            // If initial permissions is not empty, update values
            for(let resource of resourceQueries){
                let updateResource = schema.manifest.data_modeling.resource_queries.find((p) => {
                    return p.model_uid === resource.model_uid;
                });
                if(updateResource !== undefined ){
                    resource.update(updateResource);
                }
            }
        }
        this.schema = schema;
        this.remoteContent = cloneDeep(value);
        this.schema.manifest.data_modeling.models = models;
        this.schema.manifest.data_modeling.permissions = permissions;
        this.schema.manifest.data_modeling.resource_queries = resourceQueries;
        this.updatePermissionsNames();

        this.selectedModelUid = '';
        this.listRelDatatype = listRelDatatype;
        this.version = version;
        this.schemaHasError = false;
        this.modelsStructureError = [];
    }
    get modelList (){
        return this.schema.manifest.data_modeling.models;
    }
    get permissionList (){
        return this.schema.manifest.data_modeling.permissions;
    }
    get resourceQueriesList (){
        return this.schema.manifest.data_modeling.resource_queries;
    }
    get isScoped() {
        return this.permissionList.find((permission) => {
            return permission.isScoped;
        }) !== undefined;
    }
    get scopeModel() {
        // Does not work if there are several scope models
        let firstModelScopedFind = this.permissionList.find((permission) => {
            return permission.isScoped;
        });
        if(firstModelScopedFind === undefined) return {uid: null};

        let scopeModel = firstModelScopedFind.lookups;

        return this.modelList.find((m) => {
            return m.uid === scopeModel[0].model_uid;
        });
    }
    get allScopedModel() {
        if(!this.isScoped){
            return [];
        }
        return this.permissionList.filter((permission) => permission.lookups.length > 0 );
    }
    get selectedModel (){
        return this.modelList.find((model) => model.uid === this.selectedModelUid);
    }
    get relDataModelArrays (){
        return ['many_to_many_relations', 'one_to_many_relations',]; // 'one_to_one_relations'
    }
    get relDataModelArraysRelatedToRelField (){
        return{
            fk: 'one_to_many_relations',
            m2m: 'many_to_many_relations',
            // o2o: 'one_to_one_relations'
        };
    }
    get allModelFields (){
        return this.modelList.map((model) => {
            return model.fields;
        }).flat();
    }
    get allModelRelFields (){
        return this.allModelFields.filter((field) => {
            return Object.keys(this.relDataModelArraysRelatedToRelField).includes(field.datatype);
        });
    }
    async deleteCurrentModel (){

        let modelUid = this.selectedModelUid;
        let fkToModelToDelete = [];

        for(let relationArray of this.relDataModelArrays){
            for(let array of this.schema.manifest.data_modeling[relationArray]){
                if(array.target_model.uid === modelUid){
                    fkToModelToDelete.push(array.source_model);
                }
            }
        }
        if(fkToModelToDelete.length > 0){
            return Promise.reject(fkToModelToDelete);
        }

        for(let perm of this.permissionList){
            for(let array of perm.lookups){
                if(array.model_uid === modelUid){
                    return Promise.reject(array.model_name);
                }
            }
        }

        let modelToDeleteName = this.selectedModel.name;
        let relatedIndexPermission = this.permissionList.map((perm) => perm.model_uid).indexOf(modelUid);
        let relatedIndexQuery = this.resourceQueriesList.map((query) => query.model_uid).indexOf(modelUid);
        let relatedModelIndex = this.modelList.map(function(e) { return e.uid; }).indexOf(modelUid);
        this.permissionList.splice(relatedIndexPermission, 1);
        this.resourceQueriesList.splice(relatedIndexPermission, 1);
        this.deleteRelations(modelUid);
        this.modelList.splice(relatedModelIndex, 1);
        this.setSelectedModelUid('');
        return Promise.resolve(modelToDeleteName);
    }


    sortBy (list, key){
        list.sort((a,b) => {
            if(a[key] < b[key]) { return -1; }
            if(a[key] > b[key]) { return 1; }
            return 0;
        });
    }
    removeProvisionalFieldsUid (){
        for(let model of this.modelList){
            for(let field of model.fields){
                if(field.provisionalFieldUid){
                    delete field.provisionalFieldUid;
                }
            }
        }
    }
    createProvisionalFieldsUid (){
        for(let model of this.modelList){
            for(let field of model.fields){
                if(!field.provisionalFieldUid){
                    field.provisionalFieldUid = createUuidv4();
                }
            }
        }
    }

    duplicateModel (selectedDuplicateModel){
        let newModel = {
            name: selectedDuplicateModel.name,
            fields: selectedDuplicateModel.fields,
            uid: createUuidv4(),
            is_default_public: selectedDuplicateModel.is_default_public,
            description: selectedDuplicateModel.description,
            representation_field: selectedDuplicateModel.representation_field,
        };
        this.modelList.push(new Model(newModel));
        this.permissionList.push(new Permission(newModel));
        this.resourceQueriesList.push(new ResourceQueries(newModel));
    }

    createNewModel (name){
        let newModel = {
            name: name,
            fields: [],
            uid: createUuidv4(),
            is_default_public: false,
        };
        this.modelList.push(new Model(newModel));
        this.permissionList.push(new Permission(newModel));
        this.resourceQueriesList.push(new ResourceQueries(newModel));
    }

    // commenter tans que le text éditeur est désactivé
    // updateModel (value){
    //     // cette fonction est appelé par l'editor de modélsiation ace
    //     // et dans celui ci on n'affiche pas les uid (pour les maquer à l'utilisateur)
    //     // il faut donc les remettres en place au moment de l'update pour empecher les erreurs

    //     // ici on remet l'uid du model
    //     let modelUid = this.selectedModelUid;
    //     value.uid = modelUid;

    //     if(!value.hasOwnProperty('attributes') || !value.attributes){
    //         value.attributes = {};
    //     }
    //     let model = new Model(value);
    //     let relatedModelIndex = this.modelList.map(function(e) { return e.uid; }).indexOf(modelUid);
    //     this.modelList.splice(relatedModelIndex,1, model);
    //     this.deleteRelations(modelUid);
    //     for (var i = model.fields.length - 1; i >= 0; i--) {
    //         let field = model.fields[i];
    //         if (
    //             this.listRelDatatype.includes(field.datatype) &&
    //             field.hasOwnProperty('attributes') &&
    //             field.attributes.hasOwnProperty('to')
    //         ) {
    //             // ici on remet l'uid de la target de la relation car dans l'editor le nom était affiché
    //             field.attributes.to = {
    //                 name: field.attributes.to,
    //                 uid: this.modelList.find((m) => m.name === field.attributes.to).uid
    //             };
    //             this.createRelation(this.selectedModel, field);
    //         }
    //     }
    //     this.updatePermissionsNames();
    //     this.updateQueriesNames();
    // }

    deleteRelations (modelUid){
        let dataModeling = this.schema.manifest.data_modeling;
        for(let relArray of this.relDataModelArrays){
            for (let i = dataModeling[relArray].length -1; i >= 0; i--) {
                var el = dataModeling[relArray][i];
                if (el.source_model.uid === modelUid){
                    // here splice work because for loop is reversed
                    dataModeling[relArray].splice(i, 1);
                }
            }
        }
    }

    createRelation (model, field){
        let dataModeling = this.schema.manifest.data_modeling;
        let relatedRelDataModelArray = dataModeling[this.relDataModelArraysRelatedToRelField[field.datatype]];
        let targetModel = this.modelList.find((model) => model.uid === field.attributes.to.uid);
        let newRel = {
            source_field: field.name,
            source_model: {
                uid: model.uid,
                name: model.name,
            },
            target_model: {
                uid: targetModel.uid,
                name: targetModel.name,
            }
        };
        relatedRelDataModelArray.push(newRel);
    }
    commit (){
        this.sortBy(this.modelList, 'name');
        this.sortBy(this.permissionList, 'model_name');
        this.sortBy(this.resourceQueriesList, 'model_name');
        for(let model of this.modelList){
            this.sortBy(model.fields, 'name');
        }
        this.remoteContent = cloneDeep(this.schema);
    }

    reset (){
        this.schema = cloneDeep(this.remoteContent);
    }

    setSelectedModelUid (modelUid){
        this.selectedModelUid = modelUid;
    }

    checkForDuplicateFields (){
        let hasDuplicateFields = false;
        for (let model of this.modelList){
            let fieldsName = model.fields.map((f) => f.name);
            // script trouver sur le net permettant de trouver les duplicata dans un tableau
            if(filter(fieldsName, (value, index, iteratee) => includes(iteratee, value, index + 1)).length > 0){
                hasDuplicateFields = true;
                break;
            }
        }
        return hasDuplicateFields;
    }

    reCreateRelations (){
        let dataModeling = this.schema.manifest.data_modeling;
        dataModeling.one_to_many_relations = [];
        dataModeling.many_to_many_relations = [];
        for(let model of this.modelList){
            for(let field of model.fields){
                if(Object.keys(this.relDataModelArraysRelatedToRelField).includes(field.datatype)){
                    this.createRelation(model, field);
                }
            }
        }
    }

    deleteField (field){
        let i = this.selectedModel.fields.findIndex((f) => isEqual(f, field)) ;
        this.selectedModel.fields.splice(i, 1);
        if(this.selectedModel.representation_field === field.name){
            this.selectedModel.representation_field = '';
        }
        let relatedQuery = this.resourceQueriesList.find((query) => query.model_uid === this.selectedModelUid);
        relatedQuery.removeField(field.name);
        if(!this.relDataModelArraysRelatedToRelField.hasOwnProperty(field.datatype)) return;
        // Si jamais le champs suprimé est une relation alors on kill la relation en question
        let relArrayName = this.relDataModelArraysRelatedToRelField[field.datatype];
        let relArray = this.schema.manifest.data_modeling[relArrayName];

        let indexRelToRemove = relArray.findIndex((rel) => {
            return rel.source_field === field.name &&
            rel.source_model.uid === this.selectedModelUid &&
            rel.target_model.uid === field.attributes.to.uid;
        });
        relArray.splice(indexRelToRemove, 1);
    }
    updateQueriesFieldName (field, previousFieldName){
        let relatedQueries = this.resourceQueriesList.find((query) => query.model_uid === this.selectedModelUid);
        relatedQueries.updateFieldName(field.name, previousFieldName);
    }
    updateFieldRelName (field, previousRelName){
        // si il ne s'agit pas d'un champs de type relation on s'arrête
        if(!field.datatype || !this.listRelDatatype.includes(field.datatype) ) return;
        let relArrayName = this.relDataModelArraysRelatedToRelField[field.datatype];
        let relArray = this.schema.manifest.data_modeling[relArrayName];

        if(previousRelName === undefined) return; // si undefined alors n'a jamais éxisté
        let index = relArray.findIndex(
            (rel) => rel.source_field === previousRelName &&
            rel.source_model.uid === this.selectedModelUid &&
            rel.target_model === field.attributes.to
        );
        if(index < 0) return; // si pas trouver index vaut -1 et donc le target model n'a pas encore été setter
        // et le sera une fois que le field attributes.to sera changé (oir function en dessous)
        relArray[index].source_field = field.name;
    }

    updateFieldRelTarget (field, previousRelTarget){
        if(!this.listRelDatatype.includes(field.datatype) ) return;
        let relArrayName = this.relDataModelArraysRelatedToRelField[field.datatype];
        let relArray = this.schema.manifest.data_modeling[relArrayName];
        if(field.attributes.to){
            if(previousRelTarget !== undefined){
                let index = relArray.findIndex(
                    (rel) => {
                        return rel.source_field === field.name &&
                            rel.source_model.uid === this.selectedModelUid;
                    }
                );
                relArray[index].target_model = {
                    uid: field.attributes.to.uid,
                    name: field.attributes.to.name
                };
            }else{
            // si undefined alors n'a jamais existé
                relArray.push({
                    source_field: field.name,
                    source_model: {
                        uid: this.selectedModelUid,
                        name: this.selectedModel.name
                    },
                    target_model: {
                        uid: field.attributes.to.uid,
                        name: field.attributes.to.name
                    },
                });
            }
        }
    }

    updatePermissionsNames (){
        if(this.schema.manifest.data_modeling.permissions.length !== 0) {
            // If initial permissions is not empty, update values
            for(let permission of this.schema.manifest.data_modeling.permissions){
                let relatedModel = this.modelList.find((model) => model.uid === permission.model_uid);
                permission.setName(relatedModel.name);
                permission.updateLookupName(this.modelList);
            }
        }
    }
    updateQueriesNames (){
        if(this.schema.manifest.data_modeling.resource_queries.length !== 0) {
            for(let queries of this.schema.manifest.data_modeling.resource_queries){
                let relatedModel = this.modelList.find((model) => model.uid === queries.model_uid);
                queries.setName(relatedModel.name);
            }
        }
    }

    addModelStructureError (error){
        this.modelsStructureError.push(error);
    }
    resetModelStructureError (){
        this.modelsStructureError = [];
    }

    hasUnsavedChange (){
        // ici on fait cette manip sinon schema est une classe et à des valeurs supplémentaire
        // en la strigifiant elle les perd
        let localContent = JSON.parse(JSON.stringify(this.schema));
        let remoteContent = JSON.parse(JSON.stringify(this.remoteContent));
        for(let model of localContent.manifest.data_modeling.models){
            for(let field of model.fields){
                if(field.provisionalFieldUid){
                    delete field.provisionalFieldUid;
                }
            }
        }
        for(let model of remoteContent.manifest.data_modeling.models){
            for(let field of model.fields){
                if(field.provisionalFieldUid){
                    delete field.provisionalFieldUid;
                }
            }
        }

        // Sort on remoteContent to compare with localContent because localContent is already sorted. 
        this.sortBy(remoteContent.manifest.data_modeling.models, 'name');
        remoteContent.manifest.data_modeling.models.map(model=>{
            this.sortBy(model.fields, 'name');
        });
        this.sortBy(remoteContent.manifest.data_modeling.permissions, 'model_name');
        this.sortBy(remoteContent.manifest.data_modeling.resource_queries, 'model_name');

        let isSame = checkSame(remoteContent, localContent);

        return !isSame;
    }
}

export default Modeling;



// WEBPACK FOOTER //
// ./src/components/page/Instance/DataModel/schema-modelisation.js